var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { BufferGeometry } from "three";
import { default as DracoDecoderModule, } from "../../external/draco3d";
import { constructBufferForNumType, createAttributeBufferFor, numByteSize, } from "../../utils";
export class DracoLoader {
    static decodePointCloudBuffer(decodeOptions, data, target = new BufferGeometry()) {
        return __awaiter(this, void 0, void 0, function* () {
            const lib = yield DracoLoader.get();
            const buffer = new Int8Array(data.buffer, data.byteOffset, data.byteLength);
            // Assert that the data contains point cloud data
            const geometryType = lib.decoder.GetEncodedGeometryType(buffer);
            if (geometryType !== lib.module.POINT_CLOUD) {
                throw new Error("Attempting to read non point cloud draco data as a point cloud");
            }
            // Decode the point cloud data
            const pointCloud = new lib.module.PointCloud();
            const status = lib.decoder.DecodeArrayToPointCloud(buffer, buffer.byteLength, pointCloud);
            if (!status.ok()) {
                throw new Error(`Draco decoding failed with message '${status.error_msg()}'`);
            }
            for (const [attributeName, attributeOptions] of Object.entries(decodeOptions.attributes)) {
                // Convert the declared component type to the draco equivalent
                const dataType = DracoLoader.getDracoDataTypeFor(lib, attributeOptions.componentType);
                // Get the declared attribute by ID
                const attribute = lib.decoder.GetAttributeByUniqueId(pointCloud, attributeOptions.attributeId);
                // Decode the attribute into a byte buffer
                const decoded = DracoLoader.decodeAttribute(lib, pointCloud, dataType, attribute);
                // Run the user specified mapper function over each attribute value. Each value is considered as a whole
                // with all N of its components.
                if (attributeOptions.mapperFn !== undefined) {
                    // Scratch array that will be used by the mapperFn for passing in the old and returning the new values
                    const scratchArray = new Array(attributeOptions.numComponents);
                    // Iterate over each 'numComponents' block inside the decoded array
                    for (let i = 0; i < decoded.length; i += attributeOptions.numComponents) {
                        // Read the 'numComponents' block from the decoded array into the scratch to be passed into the
                        // mapper fn
                        for (let j = 0; j < attributeOptions.numComponents; j += 1) {
                            scratchArray[j] = decoded[i + j];
                        }
                        attributeOptions.mapperFn(scratchArray);
                        // Copy the results of the mapper fn back into the space in the decoded array
                        for (let j = 0; j < attributeOptions.numComponents; j += 1) {
                            decoded[i + j] = scratchArray[j];
                        }
                    }
                }
                // Add the attribute to the BufferGeometry
                target.setAttribute(attributeName, createAttributeBufferFor(decoded, attributeOptions.componentType, attributeOptions.numComponents, attributeOptions.normalized));
            }
            // Resource cleanup
            lib.module.destroy(pointCloud);
            return target;
        });
    }
    /**
     * Handles decoding a point cloud attribute into a TypedArray
     * @param lib
     * @param dracoGeometry
     * @param dataType
     * @param attribute
     * @private
     */
    static decodeAttribute(lib, dracoGeometry, dataType, attribute) {
        const numType = DracoLoader.getNumTypeFor(lib, dataType);
        // Calculate the number of bytes needed to store the decoded data
        const numComponents = attribute.num_components();
        const numPoints = dracoGeometry.num_points();
        const numValues = numPoints * numComponents;
        const byteLength = numValues * numByteSize(numType);
        // Allocate memory to decode into. Afaik this uses the WASM buffer, hence malloc.
        const ptr = lib.module._malloc(byteLength);
        // Decode the attribute into the memory we allocated
        lib.decoder.GetAttributeDataArrayForAllPoints(dracoGeometry, attribute, dataType, byteLength, ptr);
        // Constructs a TypedArray object specific to the underlying type of the attribute
        const array = constructBufferForNumType(numType, lib.module.HEAPF32.buffer, ptr, numValues).slice();
        // Free the scratch buffer used for decoding
        lib.module._free(ptr);
        return array;
    }
    /**
     * Converts a DracoComponentType variant into the matching draco DataType
     * @param lib
     * @param componentType
     * @private
     */
    static getDracoDataTypeFor(lib, componentType) {
        switch (componentType) {
            case 3 /* NumType.U64 */:
                return lib.module.DT_UINT64;
            case 7 /* NumType.I64 */:
                return lib.module.DT_INT64;
            case 0 /* NumType.U8 */:
                return lib.module.DT_UINT8;
            case 1 /* NumType.U16 */:
                return lib.module.DT_UINT16;
            case 2 /* NumType.U32 */:
                return lib.module.DT_UINT32;
            case 4 /* NumType.I8 */:
                return lib.module.DT_INT8;
            case 5 /* NumType.I16 */:
                return lib.module.DT_INT16;
            case 6 /* NumType.I32 */:
                return lib.module.DT_INT32;
            case 8 /* NumType.F32 */:
                return lib.module.DT_FLOAT32;
            case 9 /* NumType.F64 */:
                return lib.module.DT_FLOAT64;
        }
    }
    /**
     * Converts a DracoComponentType variant into the matching draco DataType
     * @param lib
     * @param componentType
     * @private
     */
    static getNumTypeFor(lib, componentType) {
        switch (componentType) {
            case lib.module.DT_UINT8:
                return 0 /* NumType.U8 */;
            case lib.module.DT_UINT16:
                return 1 /* NumType.U16 */;
            case lib.module.DT_UINT32:
                return 2 /* NumType.U32 */;
            case lib.module.DT_UINT64:
                return 3 /* NumType.U64 */;
            case lib.module.DT_INT8:
                return 4 /* NumType.I8 */;
            case lib.module.DT_INT16:
                return 5 /* NumType.I16 */;
            case lib.module.DT_INT32:
                return 6 /* NumType.I32 */;
            case lib.module.DT_INT64:
                return 7 /* NumType.I64 */;
            case lib.module.DT_FLOAT32:
                return 8 /* NumType.F32 */;
            case lib.module.DT_FLOAT64:
                return 9 /* NumType.F64 */;
            default:
                throw new Error("Unknown data type");
        }
    }
    /**
     * Initializer wrapper
     */
    static init(url) {
        return __awaiter(this, void 0, void 0, function* () {
            const wasmBinaryRequest = yield fetch(`${url || ""}draco_decoder.wasm`);
            const wasmBinary = yield wasmBinaryRequest.arrayBuffer();
            const module = yield DracoDecoderModule({ wasmBinary });
            const decoder = new module.Decoder();
            return [module, decoder];
        });
    }
    /**
     * Internal function for fetching the module and decoder, or throwing if they aren't initialized
     * @private
     */
    static get(url) {
        return __awaiter(this, void 0, void 0, function* () {
            // If the loader is already loaded then just return it immediately
            if (DracoLoader.state.loaded !== undefined) {
                return DracoLoader.state.loaded;
            }
            else {
                // If it isn't loaded we start a request to load and initialize the module
                if (DracoLoader.state.inFlight === undefined) {
                    DracoLoader.state.inFlight = this.init(url);
                }
                // Now we know the request is definitely in flight so we wait for it to resolve
                const [module, decoder] = yield DracoLoader.state.inFlight;
                // Write out the loaded loader and return it
                DracoLoader.state.loaded = {
                    module,
                    decoder,
                };
                return DracoLoader.state.loaded;
            }
        });
    }
}
DracoLoader.state = {};
